import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Row, SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import useWorkflows from 'hooks/useWorkflows';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, H2 } from 'styles/typography';
import FilterIconAlt from 'svg/FilterIconAlt';

import { INBOUND_REFERRALS_ROUTES, PORTAL_AUTOMATION_API_NAME } from './constants';
import inboundReferralsTableColumns from './inboundReferralsTableColumns';
import ReferralModal from './ReferralModal';
import { LIST_STATUS_DISPLAY, ListStatus, overallListStatus } from './referralProgress';
import { TONE_STYLES } from './StatusBadge';

type Tab = 'active' | 'history';
// The list filters by overall status; 'all' clears it. The chip row exposes the common
// subset; the Status dropdown exposes every state.
type StatusFilter = 'all' | ListStatus;

// Legend swatches — only the four surfaced states (pending/error aren't shown).
const LEGEND: ListStatus[] = ['actionNeeded', 'processing', 'readyForReview', 'complete'];

type StatusOption = { value: StatusFilter; label: string };
const STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'All' },
  { value: 'actionNeeded', label: 'Action Needed' },
  { value: 'processing', label: 'Olio Agent' },
  { value: 'readyForReview', label: 'Ready for Review' },
  { value: 'complete', label: 'Complete' },
];

export default function InboundReferralsListPage() {
  const navigate = useNavigate();
  const { workflows, isLoading, isError } = useWorkflows();

  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>([]);
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<Tab>('active');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const columns = useMemo(() => inboundReferralsTableColumns(), []);

  // This inbox shows referral-intake runs only; portal-automation runs have their own list.
  const referrals = useMemo(
    () => workflows.filter((workflow) => workflow.definition.apiName !== PORTAL_AUTOMATION_API_NAME),
    [workflows]
  );

  // History is the terminal runs (completed/canceled); Active is everything still in flight.
  const byTab = useMemo(
    () => referrals.filter((workflow) => (tab === 'history' ? workflow.isTerminal : !workflow.isTerminal)),
    [referrals, tab]
  );

  const counts = useMemo(
    () => ({
      all: byTab.length,
      actionNeeded: byTab.filter((w) => overallListStatus(w) === 'actionNeeded').length,
      readyForReview: byTab.filter((w) => overallListStatus(w) === 'readyForReview').length,
    }),
    [byTab]
  );

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return byTab
      .filter((w) => statusFilter === 'all' || overallListStatus(w) === statusFilter)
      .filter((w) => !query || w.patientName.toLowerCase().includes(query));
  }, [byTab, statusFilter, search]);

  return (
    <Page>
      {creating && <ReferralModal onClose={() => setCreating(false)} />}
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <LeftControls>
              <Title>Inbound Referrals</Title>
              <SearchBox data-testid='search-patient'>
                <Search value={search} onChange={setSearch} placeholder='Search Patient' />
              </SearchBox>
              <StatusSelect data-testid='status-filter'>
                <Select<StatusOption>
                  inputId='status-filter'
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((option) => option.value === statusFilter) ?? STATUS_OPTIONS[0]}
                  onChange={(option) => setStatusFilter(option?.value ?? 'all')}
                  getOptionValue={(option) => option.value}
                  getOptionLabel={(option) => option.label}
                  formatOptionLabel={(option, meta) =>
                    meta?.context === 'value' ? `Status: ${option.label}` : option.label
                  }
                  isSearchable={false}
                />
              </StatusSelect>
              <FiltersButton variant='ghost' size='small' data-testid='filters-button'>
                <FilterIconAlt fill={colors.blue100} />
                <span>Filters</span>
              </FiltersButton>
            </LeftControls>
            <CreateButton variant='outline-primary' onClick={() => setCreating(true)} data-testid='new-referral'>
              Create New Referral
            </CreateButton>
          </TitleRow>

          <Tabs>
            <TabButton
              type='button'
              $active={tab === 'active'}
              onClick={() => setTab('active')}
              data-testid='tab-active'>
              Active Referrals ({referrals.filter((w) => !w.isTerminal).length})
            </TabButton>
            <TabButton
              type='button'
              $active={tab === 'history'}
              onClick={() => setTab('history')}
              data-testid='tab-history'>
              History
            </TabButton>
          </Tabs>
        </HeaderInner>
      </Page.Header>
      <Page.Content>
        {tab === 'active' && (
          <Controls>
            <Chips>
              <ChipButton
                type='button'
                $active={statusFilter === 'all'}
                onClick={() => setStatusFilter('all')}
                data-testid='chip-all'>
                All ({counts.all})
              </ChipButton>
              <ChipButton
                type='button'
                $active={statusFilter === 'actionNeeded'}
                onClick={() => setStatusFilter('actionNeeded')}
                data-testid='chip-action-needed'>
                Action Needed ({counts.actionNeeded})
              </ChipButton>
              <ChipButton
                type='button'
                $active={statusFilter === 'readyForReview'}
                onClick={() => setStatusFilter('readyForReview')}
                data-testid='chip-ready'>
                Ready for Review ({counts.readyForReview})
              </ChipButton>
            </Chips>
            <Legend>
              {LEGEND.map((status) => (
                <LegendItem key={status}>
                  <Swatch $color={TONE_STYLES[LIST_STATUS_DISPLAY[status].tone].accent} />
                  {LIST_STATUS_DISPLAY[status].legendLabel ?? LIST_STATUS_DISPLAY[status].label}
                </LegendItem>
              ))}
            </Legend>
          </Controls>
        )}

        {isError ? (
          <Body>We couldn’t load referrals.</Body>
        ) : (
          <ControlledTable<Workflow>
            data={rows}
            columns={columns}
            loading={isLoading}
            pageCount={1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            onRowClick={(row: Row<Workflow>) => navigate(`${INBOUND_REFERRALS_ROUTES.BASE}/${row.original.id}`)}
          />
        )}
      </Page.Content>
    </Page>
  );
}

const HeaderInner = styled.div`
  padding: 24px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const Title = styled(H2)`
  white-space: nowrap;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 180px;
  max-width: 300px;
`;

const StatusSelect = styled.div`
  width: 180px;
  flex-shrink: 0;
`;

// The shared filters affordance (cf. FilterBar on /patients): a borderless blue
// icon + "Filters" text, not a boxed button.
const FiltersButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-weight: normal;
  color: ${colors.blue100};
  white-space: nowrap;
`;

// Match the 38px height of the shared Search pill so the two controls are the same height.
const CreateButton = styled(Button)`
  height: 38px;
  white-space: nowrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const TabButton = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 8px 0;
  cursor: pointer;
  font-weight: 500;
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black50)};
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.blue100 : 'transparent')};
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
`;

const ChipButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 16px;
  border: 1px solid ${({ $active }) => ($active ? colors.blue100 : colors.black10)};
  background: ${({ $active }) => ($active ? colors.blue10 : colors.white)};
  color: ${({ $active }) => ($active ? colors.blue100 : colors.black75)};
  font-weight: 500;
  cursor: pointer;
`;

const Legend = styled.div`
  display: flex;
  gap: 16px;
`;

const LegendItem = styled(BodySmall)`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.black50};
`;

const Swatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;
